import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getMandirs, deleteMandir, createMandir, updateMandir } from "../../../utils/api";

const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/admin\/?$/, '') : 'https://www.sanatandharmmahasabha.com');

const columns = [
  {
    key: "image",
    label: "Image",
    render: (value) => {
      const imageUrl = value ? (value.startsWith('http') ? value : `${MEDIA_BASE_URL}/media/${value}`) : null;
      return imageUrl ? (
        <div className="image-bubble">
          <img src={imageUrl} alt="Mandir" />
        </div>
      ) : (
        <div className="image-bubble placeholder">
          <span>🏛️</span>
        </div>
      );
    }
  },
  { key: "name", label: "Name" },
  { key: "address", label: "Address" },
  { key: "area", label: "Area" },
  {
    key: "headResponsible",
    label: "Head Responsible",
    render: (value) => {
      if (!value) return "-";
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        const parts = [value.name, value.phone].filter(Boolean);
        return parts.length ? parts.join(' - ') : "-";
      }
      return "-";
    }
  },
  {
    key: "buildDate",
    label: "Build Date",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
  {
    key: "createdOn",
    label: "Created On",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const filters = [
  {
    key: "area",
    label: "Filter by Area",
    type: "select",
    options: [
      { value: "Amsterdam", label: "Amsterdam" },
      { value: "Rotterdam", label: "Rotterdam" },
      { value: "Den Haag", label: "Den Haag" },
      { value: "Utrecht", label: "Utrecht" },
      { value: "Overig", label: "Overig" },
    ]
  },
];

export default function Mandirs() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMandir, setSelectedMandir] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    area: "",
    buildDate: "",
    headResponsible: "",
    prayerDays: "",
    image: ""
  });
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData({
      name: "",
      description: "",
      address: "",
      area: "",
      buildDate: "",
      headResponsible: "",
      prayerDays: "",
      image: ""
    });
    setIsEditMode(false);
    setSelectedMandir(null);
    setModalOpen(true);
  };

  const handleEdit = (mandir) => {
    setSelectedMandir(mandir);
    const headResponsibleValue = mandir.headResponsible
      ? typeof mandir.headResponsible === 'string'
        ? mandir.headResponsible
        : mandir.headResponsible.name || ''
      : '';

    setFormData({
      name: mandir.name || "",
      description: mandir.description || "",
      address: mandir.address || "",
      area: mandir.area || "",
      buildDate: mandir.buildDate ? new Date(mandir.buildDate).toISOString().split('T')[0] : "",
      headResponsible: headResponsibleValue,
      prayerDays: mandir.prayerDays || "",
      image: mandir.image || ""
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (mandir) => {
    handleEdit(mandir);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, imageUrl: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...formData,
      headResponsible: {
        name: formData.headResponsible || "",
        phone: "",
      },
    };

    try {
      if (isEditMode && selectedMandir) {
        await updateMandir(selectedMandir._id, payload);
        alert("Mandir updated successfully!");
      } else {
        await createMandir(payload);
        alert("Mandir created successfully!");
      }
      setModalOpen(false);
      window.location.reload();
    } catch (error) {
      alert(error.message || "Failed to save mandir");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Mandirs"
          fetchData={getMandirs}
          deleteData={deleteMandir}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search mandirs..."
        />
      </main>

      {/* Create/Edit Mandir Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Mandir" : "Add New Mandir"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Mandir Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                placeholder="Enter mandir name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Enter description"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-group">
              <label>Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleFormChange}
                required
                placeholder="Enter full address"
              />
            </div>
            <div className="form-group">
              <label>Area</label>
              <select
                name="area"
                value={formData.area}
                onChange={handleFormChange}
              >
                <option value="">Select Area</option>
                <option value="Amsterdam">Amsterdam</option>
                <option value="Rotterdam">Rotterdam</option>
                <option value="Den Haag">Den Haag</option>
                <option value="Utrecht">Utrecht</option>
                <option value="Overig">Overig</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h3>Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Build Date</label>
                <input
                  type="date"
                  name="buildDate"
                  value={formData.buildDate}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label>Head Responsible</label>
                <input
                  type="text"
                  name="headResponsible"
                  value={formData.headResponsible}
                  onChange={handleFormChange}
                  placeholder="Enter name"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Prayer Days</label>
              <input
                type="text"
                name="prayerDays"
                value={formData.prayerDays}
                onChange={handleFormChange}
                placeholder="e.g., Monday, Friday"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Image</h3>
            <div className="form-group">
              <label>Mandir Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update Mandir" : "Create Mandir")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
